import { Pivot, PivotItem } from "@fluentui/react";
import i18next from "../../i18n/i18n";
import { parseAnswerToHtml } from "../Answer/AnswerParser";
import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse, AppType, skipUnwantedTag } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs | undefined;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse | undefined;
    index: number;
    isAdminUser?: boolean;
    onActiveContentChanged: (citationIndex: number) => void;
    isStreaming: boolean;
    sourcePage: string;
    appType: AppType;
    isThoughtsLoading?: boolean;
    supportingContentMaxHeight?: string | number;
    supportingContentTextMaxHeight?: string | number;
    fillAvailableHeight?: boolean;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({
    answer,
    activeTab,
    activeCitation,
    citationHeight,
    className,
    onActiveTabChanged,
    index,
    isAdminUser,
    onActiveContentChanged,
    isStreaming,
    sourcePage,
    appType,
    isThoughtsLoading = false,
    supportingContentMaxHeight,
    supportingContentTextMaxHeight,
    fillAvailableHeight = false
}: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer?.context?.thoughts && !answer?.context?.has_thoughts;
    const isDisabledSupportingContentTab: boolean = !answer?.context?.data_points?.length;
    const isDisabledCitationTab: boolean = !activeCitation;
    const sanitizedThoughts = skipUnwantedTag(answer?.context?.thoughts!);
    const messageContent = typeof answer?.output_text === "string" ? answer.output_text : "";
    const dataPoints = answer?.context?.data_points ?? [];
    const fileMapping = answer?.context?.file_mapping;
    const parsedAnswer: any = parseAnswerToHtml(messageContent, isStreaming, () => {}, dataPoints, fileMapping, appType, sourcePage as "Chat" | "Feedback");
    const hasSupportingCitations = parsedAnswer?.citations?.some((citation: string) => !citation.startsWith("attachment:"));
    if (!hasSupportingCitations && activeTab === AnalysisPanelTabs.SupportingContentTab) {
        return null;
    }

    const citationIndexMap: any = new Map(parsedAnswer?.citations?.map((item: any, index: number) => [item, index]));
    const totalDataPointsSet = new Set();
    const filteredAndSorted: any = parsedAnswer?.dataPoints
        ?.filter((item: any) => {
            const key = `${item.uploaddir_field}/${item.sourcepage_field}`;
            if (totalDataPointsSet.has(key)) return false;
            totalDataPointsSet.add(key);
            return citationIndexMap.has(key) || citationIndexMap.has(item.sourcepage_field);
        })
        .sort((a: any, b: any) => {
            const aKey = `${a.uploaddir_field}/${a.sourcepage_field}`;
            const bKey = `${b.uploaddir_field}/${b.sourcepage_field}`;
            return citationIndexMap.get(aKey) - citationIndexMap.get(bKey);
        });

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            {isAdminUser && activeTab === AnalysisPanelTabs.ThoughtProcessTab && (
                <PivotItem
                    itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                    headerText={i18next.t("analysis.thoughtProcess")}
                    headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
                >
                    <SupportingContent
                        index={index}
                        supportingContent={filteredAndSorted}
                        onActiveContentChanged={onActiveContentChanged}
                        appType={appType}
                        sourcePage={sourcePage}
                        openView={AnalysisPanelTabs.ThoughtProcessTab}
                        sanitizedThoughts={sanitizedThoughts}
                        thoughtDataPoints={dataPoints}
                        isThoughtsLoading={isThoughtsLoading}
                        supportingContentMaxHeight={supportingContentMaxHeight}
                        supportingContentTextMaxHeight={supportingContentTextMaxHeight}
                        fillAvailableHeight={fillAvailableHeight}
                    />
                </PivotItem>
            )}
            {hasSupportingCitations && activeTab === AnalysisPanelTabs.SupportingContentTab && (
                <PivotItem
                    itemKey={AnalysisPanelTabs.SupportingContentTab}
                    headerText={i18next.t("analysis.supportingContent")}
                    headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
                >
                    <SupportingContent
                        index={index}
                        supportingContent={filteredAndSorted}
                        onActiveContentChanged={onActiveContentChanged}
                        appType={appType}
                        sourcePage={sourcePage}
                        openView={AnalysisPanelTabs.SupportingContentTab}
                        supportingContentMaxHeight={supportingContentMaxHeight}
                        supportingContentTextMaxHeight={supportingContentTextMaxHeight}
                        fillAvailableHeight={fillAvailableHeight}
                    />
                </PivotItem>
            )}
            {activeTab === AnalysisPanelTabs.CitationTab && (
                <PivotItem
                    itemKey={AnalysisPanelTabs.CitationTab}
                    headerText={i18next.t("analysis.citation")}
                    headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
                >
                    <iframe title={i18next.t("analysis.citation")} src={activeCitation} width="100%" height={citationHeight} />
                </PivotItem>
            )}
        </Pivot>
    );
};
